import type { ReactElement, ReactNode } from 'react';
import { Box, InputAdornment, TextField } from '@mui/material';

import { InfoTooltip } from '../../../../components/InfoTooltip';
import type { PageSeo } from '../hooks/useGetPageDetails';
import {
  SEO_DESCRIPTION_TOOLTIP,
  SEO_SOCIAL_DESCRIPTION_TOOLTIP,
  SEO_SOCIAL_TITLE_TOOLTIP,
  SEO_TITLE_TOOLTIP,
} from './seoTooltips';
import { SocialImagePicker } from './SocialImagePicker';

interface SeoFieldsProps {
  value: PageSeo;
  organisationId: string;
  onChange: (field: keyof PageSeo, value: string) => void;
  /**
   * Marks Title as required. The add-page modal gates Create on it, so the asterisk is what tells
   * the user which empty field is holding the button — everything else here, social image
   * included, is optional. The SEO settings screen has no such gate and leaves this off.
   */
  titleRequired?: boolean;
}

/**
 * The info icon for one SEO input, sitting at the end of the field rather than beside its floating
 * label. `alignTop` pins it to the first line of a multiline field, where the label is, instead of
 * letting it float halfway down the box. These fields span the card, so the explanation opens
 * above rather than to the side, where it would cover the value being explained.
 */
function FieldInfo({
  label,
  tooltip,
  alignTop = false,
}: {
  label: string;
  tooltip: ReactNode;
  alignTop?: boolean;
}): ReactElement {
  return (
    <InputAdornment position="end" className={alignTop ? 'vtwo:mt-2 vtwo:self-start' : undefined}>
      <InfoTooltip label={label} tooltip={tooltip} side="top" />
    </InputAdornment>
  );
}

// The SEO inputs shared by the SEO settings screen and the add-page modal.
export function SeoFields({
  value,
  organisationId,
  onChange,
  titleRequired = false,
}: SeoFieldsProps): ReactElement {
  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-4">
      <TextField
        label="Title"
        required={titleRequired}
        fullWidth
        size="small"
        value={value.title ?? ''}
        onChange={(event) => onChange('title', event.target.value)}
        InputProps={{
          endAdornment: <FieldInfo label="Title" tooltip={SEO_TITLE_TOOLTIP} />,
        }}
      />
      <TextField
        label="Description"
        fullWidth
        multiline
        minRows={2}
        size="small"
        value={value.description ?? ''}
        onChange={(event) => onChange('description', event.target.value)}
        InputProps={{
          endAdornment: (
            <FieldInfo label="Description" tooltip={SEO_DESCRIPTION_TOOLTIP} alignTop />
          ),
        }}
      />
      <TextField
        label="Social title"
        fullWidth
        size="small"
        value={value.socialTitle ?? ''}
        onChange={(event) => onChange('socialTitle', event.target.value)}
        InputProps={{
          endAdornment: <FieldInfo label="Social title" tooltip={SEO_SOCIAL_TITLE_TOOLTIP} />,
        }}
      />
      <TextField
        label="Social description"
        fullWidth
        multiline
        minRows={2}
        size="small"
        value={value.socialDescription ?? ''}
        onChange={(event) => onChange('socialDescription', event.target.value)}
        InputProps={{
          endAdornment: (
            <FieldInfo
              label="Social description"
              tooltip={SEO_SOCIAL_DESCRIPTION_TOOLTIP}
              alignTop
            />
          ),
        }}
      />
      <SocialImagePicker
        value={value.socialImageUrl}
        organisationId={organisationId}
        onChange={(url) => onChange('socialImageUrl', url)}
      />
    </Box>
  );
}
